import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { shallow } from 'zustand/shallow';

import {
  CreateMemoryGameSelectGamesTemplate,
  SelectGamesPath,
} from '../../components/Templates';
import {
  CreateJourneyModal,
  LoadingSpinner,
  MemoryBoostConfigModal,
  MemoryBoostInfoModal,
} from '../../components/Molecules';

import { StationSuperChallengeItemNo } from '../../constants/Games/gameStation';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { BalanceType } from '../../models/Base';
import { useDraftStore } from '../../stores/DraftStore';
import { arrayEqual } from '../../utils/array';

enum ShowPopupState {
  CreateJoruney = 'createJoruney',
  MemoryConfig = 'memoryConfig',
  ShowInfo = 'showInfo',
}
interface State {
  createType: QuickCreateGameMode | undefined;
  isNextDisabled: boolean;
  albumName: string;
  errorMessage: string | undefined;
  memoryBoost: boolean;
  memoryPeriod: number;
  showPopup: ShowPopupState | undefined;
  isLoading: boolean;
}

const MAX_LENGTH = 50;

const CreateMemoryGameSelectGamesScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({
    createType: undefined,
    isNextDisabled: false,
    albumName: '',
    errorMessage: undefined,
    memoryBoost: true,
    memoryPeriod: 55,
    showPopup: undefined,
    isLoading: false,
  });

  const { mode } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    return {
      mode: _mode,
    };
  }, shallow);

  const hasAssistedGame = useDraftStore((store) => {
    return store.getHasAssistedGame();
  }, shallow);

  const gameParts = useDraftStore((store) => {
    return store.getGameParts();
  }, arrayEqual(shallow));

  const stationDrafts = useDraftStore((store) => {
    return store.getStationDrafts();
  }, arrayEqual(shallow));

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    const handlePageShow = () => {
      navigate('/create/memory-game/select-words', { replace: true });
    };

    window.addEventListener('popstate', handlePageShow);

    return () => {
      window.removeEventListener('popstate', handlePageShow);
    };
  }, [navigate]);

  const onSwitchGamifiedTutorial = useCallback(() => {
    useDraftStore.getState().setHasAssistedGame(!hasAssistedGame);
  }, [hasAssistedGame]);

  const onChangeMemoryBoost = useCallback(
    (useMemoryBoost: boolean, period: number) => {
      setState((s) => ({
        ...s,
        memoryBoost: useMemoryBoost,
        memoryPeriod: period,
        showPopup: ShowPopupState.CreateJoruney,
      }));
    },
    [],
  );

  const onPressMemoryBoost = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.MemoryConfig }));
  }, []);

  const onAlbumNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (state.errorMessage) {
        setState((s) => ({
          ...s,
          albumName: event.target.value,
          errorMessage: undefined,
        }));
      } else {
        setState((s) => ({ ...s, albumName: event.target.value }));
      }
    },
    [state.errorMessage],
  );

  const onClickNext = useCallback(() => {
    if (!state.isNextDisabled) {
      setState((s) => ({ ...s, showPopup: ShowPopupState.CreateJoruney }));
    }
  }, [state.isNextDisabled]);

  const onClickBack = useCallback(() => {
    navigate('/create/memory-game/select-words', { replace: true });
  }, [navigate]);

  const pathStations = useMemo(() => {
    const paths: SelectGamesPath[] = [];

    gameParts.forEach((part) => {
      const stations = stationDrafts.filter(
        (station) => station.partId === part.partId && !station.hidden,
      );
      paths.push({
        gameType: part.iconTag,
        isSelected: part.isSelected,
        stations: stations,
        partId: part.partId,
      });
    });

    return paths;
  }, [gameParts, stationDrafts]);

  const onClickSelectGameType = useCallback(
    (partId: string, checked: boolean) => {
      useDraftStore.getState().changeSelectedGameType(partId, checked);
    },
    [],
  );

  const onClickSelectGame = useCallback(
    (partId: string, gameIndex: number, checked: boolean) => {
      useDraftStore.getState().changeSelectedGame(partId, gameIndex, checked);
    },
    [],
  );

  const onPressCancel = useCallback(() => {
    setState((s) => ({ ...s, showPopup: undefined }));
  }, []);

  const onPressBack = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.CreateJoruney }));
  }, []);

  const albumFee = useMemo(() => {
    return stationDrafts.filter(
      (station) => station.isSelected && !station.hidden,
    ).length;
  }, [stationDrafts]);

  const onCreateGame = useCallback(async () => {
    setState((s) => ({ ...s, showPopup: undefined, isLoading: true }));
    const result = await useDraftStore
      .getState()
      .createGames(state.albumName, state.memoryBoost, state.memoryPeriod);
    setState((s) => ({ ...s, isLoading: false }));
    if (result) {
      navigate(`/club/${result.classroomId}/journey/${result.gameSetId}`, {
        replace: true,
      });
    } else {
      toast.error(t('There is something wrong.'));
    }
  }, [navigate, state.albumName, state.memoryBoost, state.memoryPeriod, t]);

  useEffect(() => {
    const hasSelected = stationDrafts.some((station) => station.isSelected);
    if (hasSelected) {
      setState((s) => ({ ...s, isNextDisabled: false }));
    } else {
      setState((s) => ({ ...s, isNextDisabled: true }));
    }
  }, [stationDrafts]);

  const onPressCreateGame = useCallback(() => {
    if (state.albumName.length > MAX_LENGTH) {
      // do nothing
    } else if (state.albumName) {
      onCreateGame().catch(console.error);
    } else {
      setState((s) => ({ ...s, errorMessage: t('This field is required.') }));
    }
  }, [onCreateGame, state.albumName, t]);

  const onPressInfo = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.ShowInfo }));
  }, []);

  return (
    <>
      <CreateMemoryGameSelectGamesTemplate
        type={state.createType}
        isGamifiedTutorials={hasAssistedGame}
        path={pathStations}
        superChallenge={{ station: StationSuperChallengeItemNo.s1 }}
        isNextDisabled={state.isNextDisabled}
        onSwitchGamifiedTutorial={onSwitchGamifiedTutorial}
        onClickSelectGameType={onClickSelectGameType}
        onClickSelectGame={onClickSelectGame}
        onClickBack={onClickBack}
        onClickNext={onClickNext}
      />
      {state.showPopup === ShowPopupState.CreateJoruney && (
        <CreateJourneyModal
          memoryBoostDays={state.memoryBoost ? state.memoryPeriod : 0}
          title={{
            value: state.albumName,
            onChange: onAlbumNameChange,
            errorMessage: state.errorMessage,
            maxLength: MAX_LENGTH,
          }}
          currencyType={BalanceType.CreationToken}
          fee={albumFee}
          onPrimary={onPressCreateGame}
          onPressMemoryBoost={onPressMemoryBoost}
          onPressCancel={onPressCancel}
        />
      )}
      {state.showPopup === ShowPopupState.MemoryConfig && (
        <MemoryBoostConfigModal
          useMemoryBoost={state.memoryBoost}
          memoryBoostDays={state.memoryPeriod}
          onPrimary={onChangeMemoryBoost}
          onPressCancel={onPressBack}
          onPressBack={onPressBack}
          onPressInfo={onPressInfo}
        />
      )}
      {state.showPopup === ShowPopupState.ShowInfo && (
        <MemoryBoostInfoModal onClose={onPressMemoryBoost} />
      )}
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};
export default CreateMemoryGameSelectGamesScreen;
